/**
 * CLE-2661 — pVerify practice type codes offered in the Service Type dropdown.
 *
 * A practice type code selects the SET of service types pVerify asks the payer about. The numbers are
 * easy to confuse with the X12 service type codes that appear inside pVerify's own labels, and getting
 * them backwards is silent: this list used to offer "ASC Facility" as `13`, which is the SERVICE type
 * for ASC but resolves as a PRACTICE type to Hospital-InPatient. Every response to it came back
 * without an ASC block at all — strictly less ASC data than the default option returns. The practice
 * type code for a plain ASC inquiry is `28`.
 *
 * Kept short on purpose. pVerify publishes 158 codes; a dropdown of mostly-irrelevant options is how
 * the wrong one gets picked. Mirrors PRACTICE_TYPE_CATALOG in the API's eligibility domain — keep the
 * two in step.
 */

/**
 * The historical global default: ASC Facility (13) + Hospital-InPatient (48) +
 * Hospital-OutPatient (50) + Surgical-Office (2), all in one inquiry. Used when the account has no
 * `defaultPracticeTypeCode` of its own.
 */
export const DEFAULT_PRACTICE_TYPE_CODE = "189";

export const mapPayerNameToCodeForDropdown: { name: string; value: string }[] =
  [
    {
      name: "All Other Medical (ASC + Hospital In/Outpatient + Surgical)",
      value: "189",
    },
    { name: "Hospital Outpatient + Surgical Office", value: "235" },
    { name: "ASC Facility + Surgical Office", value: "185" },
    { name: "ASC Facility", value: "28" },
    { name: "Hospital Outpatient", value: "83" },
    { name: "Specialist - Office", value: "18" },
    { name: "X-Ray + MRI/CAT Scan", value: "46" },
    { name: "MRI/CAT Scan", value: "31" },
  ];

/**
 * The practice type an eligibility call should default to: the account's own setting when it has one,
 * otherwise the historical global default. Every account starts with none, so nothing changes until an
 * account is moved over deliberately.
 */
export const getDefaultPracticeTypeCode = (
  accountDefaultPracticeTypeCode?: string | null
): string => accountDefaultPracticeTypeCode || DEFAULT_PRACTICE_TYPE_CODE;

const KNOWN_PRACTICE_TYPE_CODES = new Set(
  mapPayerNameToCodeForDropdown.map((option) => option.value)
);

export const isKnownPracticeTypeCode = (code?: string | null): boolean =>
  !!code && KNOWN_PRACTICE_TYPE_CODES.has(code);

/**
 * CLE-2661 — a practice type code carried over from an existing order, or null when that code is no
 * longer one we offer.
 *
 * Redo / copy / draft seed the Service Type from the order's own recorded code so a repeat inquiry
 * asks the same question the original did. That is right for codes we still offer and wrong for the
 * ones we removed: `13` in particular is not a practice type at all — pVerify resolves it to
 * Hospital-InPatient and answers with the hospital ambulatory-surgical benefit rather than the
 * freestanding-ASC one. Roughly 370 production orders carry it.
 *
 * Carrying such a code forward is worse than merely stale, because the estimator cannot correct it:
 * a PrimeReact Dropdown holding a value absent from its options renders as the empty placeholder, so
 * the form looks blank while silently still holding the bad code. Returning null instead lets
 * PrimaryInsuranceSection / SecondaryInsuranceSection fill in the account's default, which is what a
 * brand-new order on that account would use.
 *
 * Scoped to the SPA on purpose. The monthly re-run still re-asks with the order's own recorded code
 * (`buildEligibilityRequestForOrder`), because changing the question for an estimate a patient has
 * already been sent moves their quote — a deliberate call, tracked separately. A redo or copy is a
 * NEW estimate, which is exactly the case CLE-2661 asks us not to create with the wrong code.
 */
export const knownPracticeTypeCodeOrNull = (
  code?: string | null
): string | null => (isKnownPracticeTypeCode(code) ? (code as string) : null);
